import drag from "../drag";
import zoom from "../zoom";
import { RADIUS, ARROW, STROKE_WIDTH, TEXT_SIZE } from "../constants";
import * as d3 from "d3";
import { Link } from "../../../types";
import calculateNameSize from "../../calculateNameSize";

/**
 * Draws the workspace map as an SVG using D3.js.
 *
 * TODO: remove use of any (create typings for renderChartFn in hooks/useD3.ts)
 * This is a pretty hard task, props to whoever can solve this!
 *
 * @author Juhana Kuparinen <juhana.kuparinen@kvanttori.fi>
 * @author Teemu Salonen <teemu.salonen@kvanttori.fi>
 */

export default function draw(
  simulation: d3.Simulation<any, undefined>,
  nodes: any,
  links: Link[],
  graph: any,
  svg: any,
  updateNode: (value: string) => void
) {
  const link = graph
    .append("g")
    .attr("stroke", "#666666")
    .attr("stroke-opacity", 0.6)
    .selectAll("path")
    .data(links)
    .join("path")
    .attr("marker-end", "url(#arrow)") //attach the arrow from defs
    .attr("id", (d: any) => d.id)
    .style("stroke-width", STROKE_WIDTH);

  const node = graph
    .append("g") //append a g tag in the svg
    .selectAll("g") // select all circles within the g tag (hint: there's none)
    .data(nodes) // bind data to those circles (we have node amount missing circles)
    .enter() // give me all the missing circles
    .append("g")
    .attr("id", (d: any) => `g${d.id}`);

  const circle = node
    .append("circle")
    .attr("r", RADIUS)
    .attr("cursor", "pointer")
    .attr("id", (d: any) => d.id)
    .attr("fill", (d: any) =>
      d.group === "dataset"
        ? "var(--primary-500, #253c56)"
        : d.group === "undefined"
        ? "#922a2a"
        : "var(--accent-800, #138637)"
    )
    .on("click", (d: any) => updateNode(d.target.id))
    .call(drag(simulation, node));

  const rect = node
    .append("rect") // create rectangles
    .attr("height", "4")
    .attr("fill", "#FFFF")
    .attr("opacity", "1")
    .attr("stroke-width", ".2")
    .attr("stroke", "rgba(0, 0, 0, 0.5")
    .attr("rx", 1.5)
    .attr("ry", 1.5)
    .call(drag(simulation, node));

  const text = node
    .append("text")
    .attr("font-size", `${TEXT_SIZE}rem`)
    .attr("fill", "#333333")
    .attr("font-weight", "bold")
    .text((d: any) => d.name);

  node.append("title").text((d: any) => d.name);

  // Creating a new svg def, where we store our arrow marker for later use...
  graph
    .append("svg:defs")
    .append("svg:marker")
    .attr("id", "arrow")
    .attr("viewBox", "0 -5 10 10")
    .attr("refX", 25) //so that it comes towards the center.
    .attr("markerWidth", ARROW)
    .attr("markerHeight", ARROW)
    .attr("orient", "auto")
    .append("svg:path")
    .attr("d", "M0,-5L10,0L0,5");

  graph
    .append("svg:defs")
    .append("svg:marker")
    .attr("id", "arrow-red")
    .attr("viewBox", "0 -5 10 10")
    .attr("refX", 25) //so that it comes towards the center.
    .attr("markerWidth", ARROW)
    .attr("markerHeight", ARROW)
    .attr("orient", "auto")
    .attr("fill", "red")
    .append("svg:path")
    .attr("d", "M0,-5L10,0L0,5");

  // Setting saved zoom, so graph rerender doesn't reset it
  // if(graphTransform) graph.attr('transform', graphTransform);

  const calcXPos = (d: any) =>
    d.x - d.name.length * Math.pow(0.994, d.name.length) + d.name.length / 10;

  simulation.on("tick", () => {
    link.attr(
      "d",
      (d: any) => `M${d.source.x},${d.source.y},${d.target.x},${d.target.y}`
    );

    // TODO: implement better solution for calculateNameSize
    rect
      .attr("x", (d: any) => d.x - calculateNameSize(d.name) * 0.92)
      .attr("y", (d: any) => d.y - 8)
      .attr("width", (d: any) => calculateNameSize(d.name) * 1.9);

    circle.attr("cx", (d: any) => d.x).attr("cy", (d: any) => d.y);

    text.attr("x", (d: any) => calcXPos(d)).attr("y", (d: any) => d.y - 4.8);
  });

  svg.attr("width", 500).attr("height", 600);

  // Implementing zoom and pan
  const zoomHandler = zoom(graph);
  zoomHandler(svg);
}
